import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { toneClass, type DsTone } from '@/ds/tone'
import { DsSkeleton } from '@/ds/skeleton'
import { currencyCompactStyle, currencyLocale, currencySymbol, type DsCompactStyle } from '@/ds/currency'

export type { DsCompactStyle }

// Cached per locale: building an Intl.NumberFormat is not free, and these are called per row.
const groupers = new Map<string, Intl.NumberFormat>()
const grouper = (locale: string, decimals: 0 | 2) => {
  const key = `${locale}:${decimals}`
  let f = groupers.get(key)
  if (!f) {
    f = new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    groupers.set(key, f)
  }
  return f
}

const trim1 = (n: number) => {
  const s = n.toFixed(1)
  return s.endsWith('.0') ? s.slice(0, -2) : s
}

// Digits only, no glyph (see dsMoneyText). `locale` defaults to Indian grouping (12,34,567); pass
// `currencyLocale(code)` to group by the currency the amount is actually in.
export function dsFormatMoney(amount: number, { compact = false, style = 'international', alwaysDecimals = false, locale = 'en-IN' }: { compact?: boolean; style?: DsCompactStyle; alwaysDecimals?: boolean; locale?: string } = {}): string {
  const abs = Math.abs(amount)
  const sign = amount < 0 ? '−' : ''
  if (compact && abs >= 1000) {
    if (style === 'indian') {
      if (abs >= 1e7) return `${sign}${trim1(abs / 1e7)}Cr`
      if (abs >= 1e5) return `${sign}${trim1(abs / 1e5)}L`
      return `${sign}${trim1(abs / 1e3)}K`
    }
    if (abs >= 1e9) return `${sign}${trim1(abs / 1e9)}B`
    if (abs >= 1e6) return `${sign}${trim1(abs / 1e6)}M`
    return `${sign}${trim1(abs / 1e3)}K`
  }
  const hasFraction = Math.round(abs * 100) % 100 !== 0
  return sign + grouper(locale, hasFraction || alwaysDecimals ? 2 : 0).format(abs)
}

// Use this rather than concatenating a glyph onto dsFormatMoney by hand: that is how the rupee got
// hardcoded everywhere. Grouping and abbreviation follow `currency` unless `locale` or `style` override.
export function dsMoneyText(amount: number, { currency, compact = false, style, alwaysDecimals = false, locale }: { currency?: string | null; compact?: boolean; style?: DsCompactStyle; alwaysDecimals?: boolean; locale?: string } = {}): string {
  const text = dsFormatMoney(amount, {
    compact,
    style: style ?? currencyCompactStyle(currency),
    alwaysDecimals,
    locale: locale ?? currencyLocale(currency),
  })
  return `${currencySymbol(currency)}${text}`
}

export type DsMoneySize = 'sm' | 'md' | 'lg' | 'xl'
const sizeCls: Record<DsMoneySize, string> = { sm: 'text-[14px] font-semibold', md: 'text-[17px] font-semibold', lg: 'text-[24px] font-bold tracking-[-0.5px]', xl: 'text-numeric-large' }

export interface DsMoneyProps {
  amount: number
  size?: DsMoneySize
  tone?: DsTone
  muted?: boolean
  compact?: boolean
  compactStyle?: DsCompactStyle
  topLabel?: string
  bottomLabel?: string
  leading?: ReactNode
  trailing?: ReactNode
  symbol?: string
  /** ISO 4217 code, as the backend reports it. Drives the glyph, the grouping and the compact style. */
  currency?: string | null
  showSign?: boolean
  loading?: boolean
  align?: 'start' | 'center' | 'end'
  semanticLabel?: string
  className?: string
}

export function DsMoney({ amount, size = 'md', tone, muted, compact, compactStyle, topLabel, bottomLabel, leading, trailing, symbol, currency, showSign, loading, align = 'start', semanticLabel, className }: DsMoneyProps) {
  // A multi-letter symbol already carries its own trailing space ('AED\u00A0'); a one-character glyph
  // (₹, $) has none. Normalise to exactly one non-breaking space, or none, and never add another here.
  const rawGlyph = symbol ?? currencySymbol(currency)
  const bare = rawGlyph.trimEnd()
  const glyph = bare === rawGlyph ? bare : `${bare}\u00A0`
  const signed = showSign && amount !== 0
  const colour = signed ? (amount > 0 ? toneClass.success.text : toneClass.danger.text) : tone ? toneClass[tone].text : muted ? 'text-muted-foreground' : 'text-foreground'
  // compactStyle keeps its own default, so only fall back to the currency's convention when the
  // caller left it alone.
  const text = dsFormatMoney(amount, { compact, style: compactStyle ?? currencyCompactStyle(currency), locale: currencyLocale(currency) })
  const prefix = signed && amount > 0 ? '+' : ''
  if (loading) return <DsSkeleton width={96} height={size === 'xl' ? 32 : 20} className={className} />
  return (
    <span className={cn('inline-flex flex-col', align === 'center' && 'items-center text-center', align === 'end' && 'items-end text-right', className)} aria-label={semanticLabel ?? `${prefix}${glyph}${text}`}>
      {topLabel && <span className="text-caption text-muted-foreground">{topLabel}</span>}
      <span className={cn('inline-flex items-baseline gap-1 whitespace-nowrap tabular-nums', sizeCls[size], colour)}>
        {leading}
        <span aria-hidden>
          {prefix}
          <span className="font-medium opacity-80" style={{ fontSize: '0.72em' }}>
            {glyph}
          </span>
          {text}
        </span>
        {trailing}
      </span>
      {bottomLabel && <span className="text-caption text-muted-foreground">{bottomLabel}</span>}
    </span>
  )
}
